// src/components/Social/SocialMeetupsBoard.jsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import './SocialHub.css';
import API_BASE_URL from '../../config/api';

const API_URL = API_BASE_URL;

// Debug consola
const DBG = false;

// Mientras tu backend aún lo soporte
const SEND_X_USER_ID = true;

// Debug visible UI
const SHOW_UI_DEBUG = false;

// DnD keys
const DND_MEETUP = 'application/tzone-meetup-id';
const DND_CONTACT = 'application/tzone-contact-id';

const j = (x) => {
  try {
    return JSON.stringify(x, null, 2);
  } catch {
    return String(x);
  }
};

const clog = (msg, obj) => {
  if (!DBG) return;
  // eslint-disable-next-line no-console
  console.log(`[BOARD] ${msg}${obj !== undefined ? ` :: ${j(obj)}` : ''}`);
};

const safeJson = async (res) => {
  try {
    return await res.json();
  } catch {
    return null;
  }
};

// Helpers imágenes
const isAbsUrl = (v) => typeof v === 'string' && (v.startsWith('http://') || v.startsWith('https://'));

const resolveImg = (url, role = 'athlete') => {
  const fallback =
    role === 'trainer'
      ? `${API_URL}/uploads/trainers/default.jpg`
      : `${API_URL}/uploads/athletes/default.jpg`;

  const u = typeof url === 'string' ? url.trim() : '';
  if (!u) return fallback || null;
  if (isAbsUrl(u)) return u;
  if (u.startsWith('/uploads/')) return `${API_URL}${u}`;

  const dir = role === 'trainer' ? 'trainers' : 'athletes';
  return `${API_URL}/uploads/${dir}/${u}`;
};

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        borderRadius: 999,
        padding: '8px 14px',
        border: '1px solid rgba(0,0,0,0.12)',
        background: active ? '#0f172a' : 'rgba(255,255,255,0.85)',
        color: active ? '#fff' : '#0f172a',
        fontWeight: 800,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

function DebugPanel({ lastResp, uiLogs, titleLastResponse, titleLog }) {
  return (
    <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
      <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: 12, padding: 10 }}>
        <div style={{ fontWeight: 900, marginBottom: 6 }}>{titleLastResponse}</div>
        <pre
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
          }}
        >
          {lastResp ? j(lastResp) : '—'}
        </pre>
      </div>

      <div style={{ background: 'rgba(255,255,255,0.9)', borderRadius: 12, padding: 10 }}>
        <div style={{ fontWeight: 900, marginBottom: 6 }}>{titleLog}</div>
        <pre
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: 12,
            whiteSpace: 'pre-wrap',
          }}
        >
          {(uiLogs || []).length ? uiLogs.join('\n') : '—'}
        </pre>
      </div>
    </div>
  );
}

export default function SocialMeetupsBoard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useTranslation();

  const locale = language === 'en' ? 'en-US' : 'es-CL';

  const tr = useCallback(
    (key, fallback, vars) => {
      try {
        const value = t(key, vars);
        if (!value || value === key) return fallback;
        return value;
      } catch {
        return fallback;
      }
    },
    [t]
  );

  const txt = useMemo(
    () => ({
      title: tr('social.meetupsBoard.title', 'Tablero de encuentros'),
      subtitle: tr(
        'social.meetupsBoard.subtitle',
        '1) Arrastra contactos al grupo. 2) Arrastra un encuentro al grupo.'
      ),
      loading: tr('social.meetupsBoard.loading', 'Cargando grupos, encuentros y contactos…'),

      backToCommunity: tr(
        'social.backToCommunity',
        language === 'en' ? 'Back to Community' : 'Volver a Comunidad'
      ),
      refresh: tr(
        'social.meetupsBoard.actions.refresh',
        language === 'en' ? 'Refresh' : 'Actualizar'
      ),
      refreshing: tr(
        'social.meetupsBoard.actions.refreshing',
        language === 'en' ? 'Refreshing…' : 'Actualizando…'
      ),
      reloadData: tr(
        'social.meetupsBoard.actions.reloadData',
        language === 'en' ? 'Reload data' : 'Recargar datos'
      ),
      openMeetupForm: tr(
        'social.meetupsBoard.actions.openMeetupForm',
        language === 'en'
          ? 'Open full meetup form'
          : 'Abrir formulario completo de encuentro'
      ),

      tabAll: tr('social.meetupsBoard.tabs.all', language === 'en' ? 'All' : 'Todo'),
      tabGroups: tr(
        'social.meetupsBoard.tabs.groups',
        language === 'en' ? 'My groups' : 'Mis grupos'
      ),
      tabMeetups: tr(
        'social.meetupsBoard.tabs.meetups',
        language === 'en' ? 'Meetups library' : 'Librería de encuentros'
      ),
      tabContacts: tr(
        'social.meetupsBoard.tabs.contacts',
        language === 'en' ? 'Contacts & Match' : 'Contactos y Match'
      ),

      groupsTitle: tr(
        'social.meetupsBoard.groups.title',
        language === 'en' ? 'My groups' : 'Mis grupos'
      ),
      groupsHint: tr(
        'social.meetupsBoard.groups.hint',
        language === 'en'
          ? 'Drop a contact here to add them to the group, or a meetup to associate it.'
          : 'Suelta aquí un contacto para agregarlo al grupo, o un encuentro para asociarlo.'
      ),
      createGroup: tr(
        'social.meetupsBoard.groups.create',
        language === 'en' ? 'Create group' : 'Crear grupo'
      ),
      createGroupTooltip: tr(
        'social.meetupsBoard.groups.createTooltip',
        language === 'en' ? 'Create a new group' : 'Crear un nuevo grupo'
      ),
      groupsEmpty: tr(
        'social.meetupsBoard.groups.empty',
        language === 'en' ? 'You do not have any groups yet.' : 'Aún no tienes grupos.'
      ),
      dropHere: tr(
        'social.meetupsBoard.groups.dropHere',
        language === 'en' ? 'Drop here' : 'Suelta aquí'
      ),
      dropContact: tr(
        'social.meetupsBoard.groups.dropContact',
        language === 'en' ? 'Drop to add contact' : 'Suelta para agregar contacto'
      ),
      dropMeetup: tr(
        'social.meetupsBoard.groups.dropMeetup',
        language === 'en' ? 'Drop to attach meetup' : 'Suelta para asociar encuentro'
      ),
      unnamedGroup: tr(
        'social.meetupsBoard.groups.unnamed',
        language === 'en' ? 'Unnamed group' : 'Grupo sin nombre'
      ),
      athletesCount: (count) =>
        tr(
          'social.meetupsBoard.groups.athletesCount',
          language === 'en' ? `${count} athletes` : `${count} atletas`,
          { count }
        ),
      meetupsCount: (count) =>
        tr(
          'social.meetupsBoard.groups.meetupsCount',
          language === 'en' ? `${count} meetups` : `${count} encuentros`,
          { count }
        ),
      manageMembers: tr(
        'social.meetupsBoard.groups.manageMembers',
        language === 'en' ? 'Manage members' : 'Gestionar miembros'
      ),
      closeManagement: tr(
        'social.meetupsBoard.groups.closeManagement',
        language === 'en' ? 'Close management' : 'Cerrar gestión'
      ),
      members: tr(
        'social.meetupsBoard.groups.members',
        language === 'en' ? 'Members' : 'Miembros'
      ),
      remove: tr(
        'social.meetupsBoard.groups.remove',
        language === 'en' ? 'Remove' : 'Quitar'
      ),

      meetupsTitle: tr(
        'social.meetupsBoard.meetups.title',
        language === 'en' ? 'Meetups library' : 'Librería de encuentros'
      ),
      meetupsHint: tr(
        'social.meetupsBoard.meetups.hint',
        language === 'en' ? 'Drag a meetup into a group.' : 'Arrastra un encuentro al grupo.'
      ),
      meetupsEmpty: tr(
        'social.meetupsBoard.meetups.empty',
        language === 'en'
          ? 'You have not created any meetups yet.'
          : 'Aún no has creado encuentros.'
      ),
      untitledMeetup: tr(
        'social.meetupsBoard.meetups.untitled',
        language === 'en' ? 'Untitled meetup' : 'Encuentro sin título'
      ),
      noPermissionEdit: tr(
        'social.meetupsBoard.meetups.noPermissionEdit',
        language === 'en'
          ? 'No permission (different organizer)'
          : 'Sin permisos (organizer distinto)'
      ),
      fullFormEdit: tr(
        'social.meetupsBoard.meetups.fullFormEdit',
        language === 'en' ? 'Edit in full form' : 'Editar en formulario completo'
      ),
      deleteMeetupTitle: tr(
        'social.meetupsBoard.meetups.deleteTitle',
        language === 'en' ? 'Delete meetup' : 'Eliminar encuentro'
      ),
      dragToGroup: tr(
        'social.meetupsBoard.meetups.dragToGroup',
        language === 'en' ? 'Drag into a group →' : 'Arrastra hacia un grupo →'
      ),
      thisMeetup: tr(
        'social.meetupsBoard.meetups.thisMeetup',
        language === 'en' ? 'this meetup' : 'este encuentro'
      ),

      contactsTitle: tr(
        'social.meetupsBoard.contacts.title',
        language === 'en' ? 'Contacts & Match' : 'Contactos y Match'
      ),
      contactsHint: tr(
        'social.meetupsBoard.contacts.hint',
        language === 'en'
          ? 'Drag a contact into a group to add them.'
          : 'Arrastra un contacto al grupo para agregarlo.'
      ),
      contactsEmpty: tr(
        'social.meetupsBoard.contacts.empty',
        language === 'en'
          ? 'As you create matches, they will appear here.'
          : 'A medida que generes matches, aparecerán aquí.'
      ),
      athleteFallback: tr(
        'social.meetupsBoard.contacts.athleteFallback',
        language === 'en' ? 'Tzone athlete' : 'Atleta Tzone'
      ),
      origin: tr(
        'social.meetupsBoard.contacts.origin',
        language === 'en' ? 'Source' : 'Origen'
      ),
      unknownOrigin: tr(
        'social.meetupsBoard.contacts.unknownOrigin',
        language === 'en' ? 'unknown' : 'desconocido'
      ),
      originTitle: (source) =>
        tr(
          'social.meetupsBoard.contacts.originTitle',
          language === 'en' ? `Source: ${source}` : `Origen: ${source}`,
          { source }
        ),
      matchSource: tr(
        'social.meetupsBoard.contacts.matchSource',
        'Match'
      ),
      nextStep: tr(
        'social.meetupsBoard.contacts.nextStep',
        language === 'en'
          ? 'Next step: add “Coached” athletes (trainer athlete list) and show both sources.'
          : 'Próximo paso: sumar “Entrenado” (lista de atletas del entrenador) y mostrar ambos orígenes.'
      ),

      createGroupModal: tr(
        'social.meetupsBoard.modals.createGroup',
        language === 'en' ? 'Create group' : 'Crear grupo'
      ),
      editGroupModal: tr(
        'social.meetupsBoard.modals.editGroup',
        language === 'en' ? 'Edit group' : 'Editar grupo'
      ),
      groupName: tr(
        'social.meetupsBoard.modals.groupName',
        language === 'en' ? 'Name' : 'Nombre'
      ),
      description: tr(
        'social.meetupsBoard.modals.description',
        language === 'en' ? 'Description' : 'Descripción'
      ),
      backendNote: tr(
        'social.meetupsBoard.modals.backendNote',
        language === 'en'
          ? 'Note: if your backend does not yet support PUT/POST /api/social/groups, you will get an HTTP error.'
          : 'Nota: si tu backend aún no tiene PUT/POST /api/social/groups, te indicará error HTTP.'
      ),
      deleteGroupTitle: tr(
        'social.meetupsBoard.modals.deleteGroupTitle',
        language === 'en' ? 'Delete group' : 'Eliminar grupo'
      ),
      deleteGroupConfirm: (name) =>
        tr(
          'social.meetupsBoard.modals.deleteGroupConfirm',
          language === 'en'
            ? `Are you sure you want to delete ${name}?`
            : `¿Seguro que deseas eliminar ${name}?`,
          { name }
        ),
      deleteMeetupConfirm: (title) =>
        tr(
          'social.meetupsBoard.modals.deleteMeetupConfirm',
          language === 'en'
            ? `Are you sure you want to delete ${title}?`
            : `¿Seguro que deseas eliminar ${title}?`,
          { title }
        ),

      loadingGroupsError: (status) =>
        tr(
          'social.meetupsBoard.messages.loadingGroupsError',
          language === 'en'
            ? `Error loading groups (HTTP ${status})`
            : `Error al cargar grupos (HTTP ${status})`,
          { status }
        ),
      attachingMeetup: tr(
        'social.meetupsBoard.messages.attachingMeetup',
        language === 'en' ? '⏳ Attaching meetup to group…' : '⏳ Asociando encuentro al grupo…'
      ),
      meetupAttached: tr(
        'social.meetupsBoard.messages.meetupAttached',
        language === 'en' ? '✅ Meetup attached to group.' : '✅ Encuentro asociado al grupo.'
      ),
      addingContact: (name) =>
        tr(
          'social.meetupsBoard.messages.addingContact',
          language === 'en'
            ? `⏳ Adding "${name}" to group…`
            : `⏳ Agregando "${name}" al grupo…`,
          { name }
        ),
      contactAdded: (name) =>
        tr(
          'social.meetupsBoard.messages.contactAdded',
          language === 'en'
            ? `✅ "${name}" added to group.`
            : `✅ "${name}" agregado al grupo.`,
          { name }
        ),
      draggedContactNotFound: tr(
        'social.meetupsBoard.messages.draggedContactNotFound',
        language === 'en'
          ? 'Dragged contact was not found.'
          : 'No encontré el contacto arrastrado.'
      ),
      dropWithoutPayload: tr(
        'social.meetupsBoard.messages.dropWithoutPayload',
        language === 'en'
          ? 'DROP arrived without payload (no meetupId or contactId).'
          : 'DROP llegó sin payload (ni meetupId ni contactId).'
      ),
      groupNameRequired: tr(
        'social.meetupsBoard.messages.groupNameRequired',
        language === 'en' ? 'Group name is required.' : 'El grupo debe tener nombre.'
      ),
      groupCreated: tr(
        'social.meetupsBoard.messages.groupCreated',
        language === 'en' ? '✅ Group created.' : '✅ Grupo creado.'
      ),
      groupUpdated: tr(
        'social.meetupsBoard.messages.groupUpdated',
        language === 'en' ? '✅ Group updated.' : '✅ Grupo actualizado.'
      ),
      groupDeleted: tr(
        'social.meetupsBoard.messages.groupDeleted',
        language === 'en' ? '✅ Group deleted.' : '✅ Grupo eliminado.'
      ),
      meetupDeleted: tr(
        'social.meetupsBoard.messages.meetupDeleted',
        language === 'en' ? '✅ Meetup deleted.' : '✅ Encuentro eliminado.'
      ),

      edit: language === 'en' ? 'Edit' : 'Editar',
      delete: tr('common.delete', language === 'en' ? 'Delete' : 'Eliminar'),
      cancel: tr('common.cancel', language === 'en' ? 'Cancel' : 'Cancelar'),
      save: tr('common.save', language === 'en' ? 'Save' : 'Guardar'),
      createMeetup: tr(
        'social.organizeMeetup',
        language === 'en' ? 'Organize meetup' : 'Organizar encuentro'
      ),
      createFullMeetup:
        language === 'en' ? 'Create meetup with full form' : 'Crear encuentro con el formulario completo',
      removeMemberLoading:
        language === 'en' ? '⏳ Removing member...' : '⏳ Quitando miembro...',
      memberRemoved:
        language === 'en' ? '✅ Member removed.' : '✅ Miembro removido.',
      missingMemberId:
        language === 'en' ? 'Empty memberId.' : 'memberId vacío.',
      missingContactUserId:
        language === 'en'
          ? 'Could not determine contact ID (empty userId).'
          : 'No pude determinar el ID del contacto (userId vacío).',
      noAddMemberEndpoint:
        language === 'en'
          ? 'No add-member endpoint available'
          : 'No existe endpoint de add member',
      memberLabel: language === 'en' ? 'Member' : 'Miembro',
      lastResponse: language === 'en' ? 'Last response' : 'Última respuesta',
      log: 'Log',
    }),
    [tr, language]
  );

  const [groups, setGroups] = useState([]);
  const [meetups, setMeetups] = useState([]);
  const [contacts, setContacts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [draggingMeetupId, setDraggingMeetupId] = useState(null);
  const [draggingContactId, setDraggingContactId] = useState(null);
  const [groupHoverId, setGroupHoverId] = useState(null);
  const [hoverMode, setHoverMode] = useState(null);
  const isDragging = Boolean(draggingMeetupId || draggingContactId);

  const [uiLogs, setUiLogs] = useState([]);
  const [lastResp, setLastResp] = useState(null);

  const [manageGroupId, setManageGroupId] = useState(null);
  const [viewMode, setViewMode] = useState('all');

  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [editGroup, setEditGroup] = useState(null);
  const [deleteGroup, setDeleteGroup] = useState(null);
  const [groupForm, setGroupForm] = useState({ name: '', description: '' });

  const [deleteMeetup, setDeleteMeetup] = useState(null);

  const groupsRef = useRef(null);
  const meetupsRef = useRef(null);
  const contactsRef = useRef(null);

  const pushUILog = useCallback(
    (m) => {
      if (!SHOW_UI_DEBUG) return;
      setUiLogs((prev) => [`[${new Date().toLocaleTimeString(locale)}] ${m}`, ...(prev || [])].slice(0, 60));
    },
    [locale]
  );

  const authInfo = useMemo(() => {
    const token = localStorage.getItem('token') || '';
    const userId = (localStorage.getItem('userId') || localStorage.getItem('userID') || '').trim();
    return { token, userId };
  }, []);

  const authHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (authInfo.token) headers.Authorization = `Bearer ${authInfo.token}`;
    if (SEND_X_USER_ID && authInfo.userId) headers['x-user-id'] = authInfo.userId;
    return headers;
  }, [authInfo.token, authInfo.userId]);

  const scrollToMode = useCallback((mode) => {
    const m = String(mode || '').toLowerCase();
    const map = {
      groups: groupsRef,
      meetups: meetupsRef,
      contacts: contactsRef,
    };
    const ref = map[m];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useEffect(() => {
    const h = String(location.hash || '')
      .replace('#', '')
      .trim()
      .toLowerCase();

    const allowed = new Set(['all', 'groups', 'meetups', 'contacts']);
    const next = allowed.has(h) ? h : 'all';

    setViewMode(next);

    if (next !== 'all') {
      setTimeout(() => scrollToMode(next), 60);
    }
  }, [location.hash, scrollToMode]);

  const setMode = useCallback(
    (mode) => {
      const m = String(mode || 'all').toLowerCase();
      setViewMode(m);
      navigate({ pathname: location.pathname, hash: `#${m}` }, { replace: true });

      if (m !== 'all') {
        setTimeout(() => scrollToMode(m), 60);
      }
    },
    [navigate, location.pathname, scrollToMode]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const gRes = await fetch(`${API_URL}/api/social/groups/mine`, {
        headers: authHeaders(),
      });
      const gJson = await safeJson(gRes);

      if (!gRes.ok) {
        throw new Error(gJson?.message || txt.loadingGroupsError(gRes.status));
      }

      const groupsArr = Array.isArray(gJson) ? gJson : [];
      setGroups(groupsArr);

      let allMeetups = [];
      if (groupsArr.length > 0) {
        const perGroup = await Promise.all(
          groupsArr.map(async (g) => {
            try {
              const evRes = await fetch(`${API_URL}/api/social/events/by-group/${g._id}`, {
                headers: authHeaders(),
              });

              if (!evRes.ok) return [];

              const evJson = await safeJson(evRes);
              const evArr = Array.isArray(evJson) ? evJson : [];

              return evArr.map((ev) => ({
                ...ev,
                groupId: g._id,
                groupName: g.name || '',
              }));
            } catch {
              return [];
            }
          })
        );

        allMeetups = perGroup.flat();
      }

      setMeetups(allMeetups);

      const cRes = await fetch(`${API_URL}/api/community/matches?status=active`, {
        headers: authHeaders(),
      });

      if (cRes.ok) {
        const cJson = await safeJson(cRes);
        const rawContacts = Array.isArray(cJson) ? cJson : [];

        const mapped = rawContacts.map((m) => {
          const other = m.otherUser || m.partner || m.target || m.contact || m.user || {};
          const name = other.displayName || other.name || m.otherName || m.partnerName || txt.athleteFallback;
          const role = other.role || 'athlete';
          const photoUrlRaw = other.photoUrl || other.avatarUrl || other.profilePhoto || m.photoUrl || '';
          const otherUserId = other._id || other.id || other.userId || other.userID || null;

          return {
            _id: m._id || m.id || String(otherUserId || name),
            userId: otherUserId ? String(otherUserId) : null,
            name,
            displayName: name,
            city: other.city || m.city || '',
            sport: other.sport || (Array.isArray(other.sports) ? other.sports[0] : '') || '',
            role,
            photoUrl: resolveImg(photoUrlRaw, role),
            source: 'match',
            sourceLabel: txt.matchSource,
            raw: m,
          };
        });

        setContacts(mapped);
      } else {
        pushUILog(`WARN matches HTTP ${cRes.status} (mantengo lista anterior)`);
      }

      clog('loadData OK', {
        groups: groupsArr.length,
        meetups: allMeetups.length,
      });

      pushUILog(`loadData OK grupos=${groupsArr.length} meetups=${allMeetups.length}`);
    } catch (err) {
      const msg = String(err?.message || err);
      setError(msg);
      pushUILog(`ERROR loadData: ${msg}`);
      clog('loadData ERROR', msg);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, pushUILog, txt]);

  const didInit = useRef(false);

  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    loadData();
  }, [loadData]);

  const removeMemberFromGroup = async (groupId, memberId) => {
    if (!memberId) throw new Error(txt.missingMemberId);

    const url = `${API_URL}/api/social/groups/${groupId}/members/${memberId}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: authHeaders(),
    });

    const data = await safeJson(res);
    const summary = {
      op: 'remove-member',
      url,
      status: res.status,
      ok: res.ok,
      data,
    };

    if (SHOW_UI_DEBUG) setLastResp(summary);
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

    return data;
  };

  const attachMeetupToGroup = async (groupId, meetupId) => {
    const payload = { meetupId, sendInvites: true };
    const url = `${API_URL}/api/social/groups/${groupId}/attach-meetup`;

    const res = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await safeJson(res);
    const summary = {
      op: 'attach-meetup',
      url,
      status: res.status,
      ok: res.ok,
      data,
    };

    if (SHOW_UI_DEBUG) setLastResp(summary);
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

    return data;
  };

  const addMemberToGroup = async (groupId, contact) => {
    const memberId = contact?.userId || null;
    if (!memberId) throw new Error(txt.missingContactUserId);

    const candidates = [
      { url: `${API_URL}/api/social/groups/${groupId}/add-member`, body: { memberId } },
      { url: `${API_URL}/api/social/groups/${groupId}/add-member`, body: { athleteId: memberId } },
      { url: `${API_URL}/api/social/groups/${groupId}/add-athlete`, body: { athleteId: memberId } },
      { url: `${API_URL}/api/social/groups/${groupId}/members`, body: { memberId } },
    ];

    let last = null;

    for (const c of candidates) {
      const res = await fetch(c.url, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(c.body),
      });

      const data = await safeJson(res);

      last = {
        op: 'add-member',
        url: c.url,
        status: res.status,
        ok: res.ok,
        data,
      };

      if (SHOW_UI_DEBUG) setLastResp(last);
      if (res.ok) return data;
    }

    const msg =
      last?.data?.message ||
      `${txt.noAddMemberEndpoint} (último HTTP ${last?.status})`;
    throw new Error(msg);
  };

  const createGroupApi = async (payload) => {
    const url = `${API_URL}/api/social/groups`;
    const res = await fetch(url, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await safeJson(res);
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

    return data;
  };

  const updateGroupApi = async (groupId, payload) => {
    const url = `${API_URL}/api/social/groups/${groupId}`;
    const res = await fetch(url, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await safeJson(res);
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

    return data;
  };

  const deleteGroupApi = async (groupId) => {
    const url = `${API_URL}/api/social/groups/${groupId}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: authHeaders(),
    });

    const data = await safeJson(res);
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

    return data;
  };

  const deleteMeetupApi = async (eventId) => {
    const url = `${API_URL}/api/social/events/${eventId}`;
    const res = await fetch(url, {
      method: 'DELETE',
      headers: authHeaders(),
    });

    const data = await safeJson(res);
    if (!res.ok) throw new Error(data?.message || `HTTP ${res.status}`);

    return data;
  };

  const lastDropRef = useRef({ key: '', ts: 0 });

  const isDupDrop = (key, msWindow = 50) => {
    const now = Date.now();
    if (lastDropRef.current.key === key && now - lastDropRef.current.ts < msWindow) {
      return true;
    }
    lastDropRef.current = { key, ts: now };
    return false;
  };

  const handleDragStartMeetup = (e, meetupId) => {
    setDraggingMeetupId(meetupId);
    setDraggingContactId(null);
    setHoverMode('meetup');

    e.dataTransfer.setData('text/plain', String(meetupId));
    e.dataTransfer.setData(DND_MEETUP, String(meetupId));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragStartContact = (e, contactId) => {
    setDraggingContactId(contactId);
    setDraggingMeetupId(null);
    setHoverMode('contact');

    e.dataTransfer.setData('text/plain', String(contactId));
    e.dataTransfer.setData(DND_CONTACT, String(contactId));
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDragEnd = () => {
    setDraggingMeetupId(null);
    setDraggingContactId(null);
    setGroupHoverId(null);
    setHoverMode(null);
  };

  const handleGroupDragOver = (e, groupId) => {
    e.preventDefault();

    if (groupHoverId !== groupId) {
      setGroupHoverId(groupId);
    }

    if (hoverMode === 'contact') e.dataTransfer.dropEffect = 'copy';
    if (hoverMode === 'meetup') e.dataTransfer.dropEffect = 'move';
  };

  const handleGroupDrop = async (e, groupId) => {
    e.preventDefault();

    const meetupId = e.dataTransfer.getData(DND_MEETUP) || '';
    const contactId = e.dataTransfer.getData(DND_CONTACT) || '';
    const plain = e.dataTransfer.getData('text/plain') || '';

    const meetupFinal = meetupId || (hoverMode === 'meetup' ? plain : '');
    const contactFinal = contactId || (hoverMode === 'contact' ? plain : '');

    const dropKey = `${groupId}::${meetupFinal || '-'}::${contactFinal || '-'}::${hoverMode || '-'}`;
    if (isDupDrop(dropKey, 80)) return;

    setMessage('');
    setError('');

    try {
      if (meetupFinal) {
        setMessage(txt.attachingMeetup);
        const result = await attachMeetupToGroup(groupId, meetupFinal);

        // Mensaje real segun lo que paso, no un generico "listo" - asi
        // se ve de una si el grupo no tenia a quien avisar, si ya
        // estaban todos anotados, o si de verdad se sumo gente nueva.
        const invitedList = Array.isArray(result?.invited) ? result.invited : [];
        const newlyJoined = invitedList.filter((i) => i.autoJoined && !i.waitlisted).length;
        const waitlisted = invitedList.filter((i) => i.autoJoined && i.waitlisted).length;
        const already = invitedList.filter((i) => i.alreadyParticipant).length;
        const failedCount = Array.isArray(result?.failed) ? result.failed.length : 0;

        if (invitedList.length === 0) {
          setMessage(
            language === 'en'
              ? '✅ Meetup attached. This group has no other members to notify yet — drag contacts into it first.'
              : '✅ Encuentro asociado. Este grupo todavía no tiene otros miembros a quien avisar — arrastra contactos aquí primero.'
          );
        } else if (newlyJoined === 0 && waitlisted === 0) {
          setMessage(
            language === 'en'
              ? `✅ Meetup attached. All ${already} group member(s) were already in this event.`
              : `✅ Encuentro asociado. Los ${already} miembro(s) del grupo ya estaban anotados en este encuentro.`
          );
        } else {
          const parts = [];
          if (newlyJoined) parts.push(language === 'en' ? `${newlyJoined} added` : `${newlyJoined} agregados`);
          if (waitlisted) parts.push(language === 'en' ? `${waitlisted} waitlisted` : `${waitlisted} en lista de espera`);
          if (already) parts.push(language === 'en' ? `${already} already in` : `${already} ya estaban`);
          if (failedCount) parts.push(language === 'en' ? `${failedCount} failed` : `${failedCount} fallaron`);
          setMessage(`✅ ${txt.meetupAttached.replace(/^✅\s*/, '')} (${parts.join(', ')})`);
        }

        await loadData();
        return;
      }

      if (contactFinal) {
        const contact = (contacts || []).find((c) => String(c._id) === String(contactFinal));
        if (!contact) throw new Error(txt.draggedContactNotFound);

        setMessage(txt.addingContact(contact.name));
        await addMemberToGroup(groupId, contact);
        setMessage(txt.contactAdded(contact.name));
        await loadData();
        return;
      }

      setError(txt.dropWithoutPayload);
    } catch (err) {
      setMessage('');
      setError(String(err?.message || err));
    } finally {
      setDraggingMeetupId(null);
      setDraggingContactId(null);
      setGroupHoverId(null);
      setHoverMode(null);
    }
  };

  const showGroups = viewMode === 'all' || viewMode === 'groups';
  const showMeetups = viewMode === 'all' || viewMode === 'meetups';
  const showContacts = viewMode === 'all' || viewMode === 'contacts';

  const myUserId = String(authInfo.userId || '');

  return (
    <div className="social-meetups-root">
      <div className="social-meetups-inner">
        <header className="social-meetups-header">
          <div>
            <h1>{txt.title}</h1>
            <p>{txt.subtitle}</p>
          </div>
        </header>

        <div
          style={{
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            alignItems: 'center',
            margin: '10px 0 12px',
          }}
        >
          <button
            type="button"
            className="social-cta"
            onClick={() => navigate('/social')}
            style={{ borderRadius: 999 }}
            title={txt.backToCommunity}
          >
            ← {txt.backToCommunity}
          </button>

          <button
            type="button"
            className="social-cta-secondary"
            onClick={loadData}
            disabled={loading}
            style={{ borderRadius: 999 }}
            title={txt.reloadData}
          >
            {loading ? txt.refreshing : txt.refresh}
          </button>

          <button
            type="button"
            className="social-cta-secondary"
            onClick={() => navigate('/social/meetups', { state: { from: 'board' } })}
            style={{ borderRadius: 999 }}
            title={txt.openMeetupForm}
          >
            + {txt.createMeetup}
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16 }}>
          <TabButton active={viewMode === 'all'} onClick={() => setMode('all')}>
            {txt.tabAll}
          </TabButton>
          <TabButton active={viewMode === 'groups'} onClick={() => setMode('groups')}>
            {txt.tabGroups}
          </TabButton>
          <TabButton active={viewMode === 'meetups'} onClick={() => setMode('meetups')}>
            {txt.tabMeetups}
          </TabButton>
          <TabButton active={viewMode === 'contacts'} onClick={() => setMode('contacts')}>
            {txt.tabContacts}
          </TabButton>
        </div>

        {message && (
          <div className="social-meetups-message">
            <span>✅</span> <span>{message}</span>
          </div>
        )}

        {error && (
          <div className="social-meetups-message" style={{ background: '#b91c1c' }}>
            <span>⚠️</span> <span>{error}</span>
          </div>
        )}

        {SHOW_UI_DEBUG && (
          <DebugPanel
            lastResp={lastResp}
            uiLogs={uiLogs}
            titleLastResponse={txt.lastResponse}
            titleLog={txt.log}
          />
        )}

        {loading ? (
          <p>{txt.loading}</p>
        ) : (
          <div className="social-meetups-layout">
            {showGroups && (
              <section ref={groupsRef} className="social-meetups-column">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div>
                    <h2 style={{ marginBottom: 6 }}>{txt.groupsTitle}</h2>
                    <p className="social-meetups-subtext" style={{ marginTop: 0 }}>
                      {txt.groupsHint}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="social-cta-secondary"
                    style={{ borderRadius: 999 }}
                    onClick={() => {
                      setError('');
                      setMessage('');
                      setGroupForm({ name: '', description: '' });
                      setEditGroup(null);
                      setShowCreateGroup(true);
                    }}
                    title={txt.createGroupTooltip}
                  >
                    + {txt.createGroup}
                  </button>
                </div>

                <div className="social-meetups-groups-list">
                  {groups.length === 0 && <p className="social-meetups-placeholder">{txt.groupsEmpty}</p>}

                  {groups.map((g) => {
                    const hover = groupHoverId === g._id;
                    const label =
                      hoverMode === 'contact'
                        ? txt.dropContact
                        : hoverMode === 'meetup'
                        ? txt.dropMeetup
                        : txt.dropHere;

                    const isOwner = String(g.owner || '') === myUserId;

                    const uniqueMembersAll = Array.from(
                      new Map(
                        (Array.isArray(g.memberDetails) ? g.memberDetails : [])
                          .map((m) => [String(m?._id || m?.userId || m?.id || ''), m])
                          .filter(([k]) => !!k)
                      ).values()
                    );

                    const uniqueMembers = uniqueMembersAll.filter(
                      (m) => String(m?._id || m?.userId || m?.id || '') !== myUserId
                    );

                    const membersCountShown = uniqueMembers.length;

                    return (
                      <div
                        key={g._id}
                        className={`social-meetups-group-item${hover ? ' social-meetups-group-item--hover' : ''}`}
                        style={{
                          outline: hover ? '3px solid rgba(37, 99, 235, 0.25)' : 'none',
                          position: 'relative',
                        }}
                        title={label}
                      >
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            borderRadius: 12,
                            zIndex: 50,
                            background: 'transparent',
                            pointerEvents: isDragging ? 'auto' : 'none',
                          }}
                          onDragOver={(e) => handleGroupDragOver(e, g._id)}
                          onDrop={(e) => handleGroupDrop(e, g._id)}
                          onDragEnter={() => setGroupHoverId(g._id)}
                          onDragLeave={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget)) {
                              setGroupHoverId(null);
                            }
                          }}
                          aria-label="dropzone"
                        />

                        <div style={{ position: 'relative', zIndex: 1 }}>
                          <div className="social-meetups-group-name">{g.name || txt.unnamedGroup}</div>

                          <div className="social-meetups-group-meta">
                            <span>{txt.athletesCount(membersCountShown)}</span>
                            {g.meetupsCount != null && <span> · {txt.meetupsCount(g.meetupsCount)}</span>}
                          </div>

                          {isOwner && (
                            <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <button
                                type="button"
                                className="social-cta-secondary"
                                style={{ borderRadius: 999 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setError('');
                                  setMessage('');
                                  setEditGroup(g);
                                  setGroupForm({
                                    name: g?.name || '',
                                    description: g?.description || '',
                                  });
                                  setShowCreateGroup(false);
                                }}
                              >
                                {txt.edit}
                              </button>

                              <button
                                type="button"
                                className="social-cta-secondary"
                                style={{ borderRadius: 999 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setError('');
                                  setMessage('');
                                  setDeleteGroup({
                                    _id: g._id,
                                    name: g?.name || txt.unnamedGroup,
                                  });
                                }}
                              >
                                {txt.delete}
                              </button>
                            </div>
                          )}

                          {isOwner && (
                            <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                              <button
                                type="button"
                                className="social-cta-secondary"
                                style={{ borderRadius: 999 }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setManageGroupId((prev) => (prev === g._id ? null : g._id));
                                }}
                              >
                                {manageGroupId === g._id ? txt.closeManagement : txt.manageMembers}
                              </button>
                            </div>
                          )}

                          {manageGroupId === g._id && isOwner && (
                            <div
                              style={{
                                marginTop: 10,
                                padding: 10,
                                borderRadius: 12,
                                background: 'rgba(255,255,255,0.92)',
                                border: '1px solid rgba(0,0,0,0.06)',
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <div style={{ fontWeight: 900, marginBottom: 8 }}>{txt.members}</div>

                              {(uniqueMembersAll || [])
                                .filter((m) => {
                                  const memberId = String(m?._id || m?.userId || m?.id || '');
                                  return memberId && memberId !== String(g.owner || '');
                                })
                                .map((m) => {
                                  const mid = String(m?._id || m?.userId || m?.id || '');
                                  const labelName = m?.displayName || m?.name || mid;

                                  return (
                                    <div
                                      key={mid}
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '6px 0',
                                        borderBottom: '1px dashed rgba(0,0,0,0.08)',
                                      }}
                                    >
                                      <div
                                        style={{
                                          fontSize: 13,
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                        }}
                                      >
                                        {labelName}
                                      </div>

                                      <button
                                        type="button"
                                        className="social-cta-secondary"
                                        style={{ borderRadius: 999 }}
                                        onClick={async (e) => {
                                          e.preventDefault();
                                          e.stopPropagation();

                                          try {
                                            setError('');
                                            setMessage(txt.removeMemberLoading);
                                            await removeMemberFromGroup(g._id, mid);
                                            setMessage(txt.memberRemoved);
                                            await loadData();
                                          } catch (err) {
                                            setMessage('');
                                            setError(String(err?.message || err));
                                          }
                                        }}
                                      >
                                        {txt.remove}
                                      </button>
                                    </div>
                                  );
                                })}
                            </div>
                          )}

                          {uniqueMembers.length > 0 && (
                            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                              {uniqueMembers.slice(0, 6).map((mm) => {
                                const src = resolveImg(mm.photoUrl || mm.avatarUrl || null, mm.role || 'athlete');

                                return (
                                  <div
                                    key={String(mm._id || mm.userId || mm.id)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 6,
                                      padding: '4px 8px',
                                      borderRadius: 999,
                                      background: 'rgba(255,255,255,0.75)',
                                      fontSize: 12,
                                      maxWidth: 200,
                                    }}
                                  >
                                    <img
                                      src={src || undefined}
                                      alt={mm.displayName || mm.name || txt.memberLabel}
                                      style={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        objectFit: 'cover',
                                      }}
                                      onError={(e) => {
                                        e.currentTarget.src = resolveImg(null, mm.role || 'athlete') || '';
                                      }}
                                      draggable={false}
                                    />
                                    <span
                                      style={{
                                        maxWidth: 140,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {mm.displayName || mm.name || txt.memberLabel}
                                    </span>
                                  </div>
                                );
                              })}

                              {uniqueMembers.length > 6 && (
                                <div style={{ fontSize: 12, fontWeight: 900, opacity: 0.85 }}>
                                  +{uniqueMembers.length - 6}
                                </div>
                              )}
                            </div>
                          )}

                          <div style={{ marginTop: 8, fontWeight: 800, opacity: 0.85 }}>{label}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {showMeetups && (
              <section ref={meetupsRef} className="social-meetups-column">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div>
                    <h2 style={{ marginBottom: 6 }}>{txt.meetupsTitle}</h2>
                    <p className="social-meetups-subtext" style={{ marginTop: 0 }}>
                      {txt.meetupsHint}
                    </p>
                  </div>

                  <button
                    type="button"
                    className="social-cta-secondary"
                    style={{ borderRadius: 999 }}
                    onClick={() => {
                      setError('');
                      setMessage('');
                      navigate('/social/meetups', { state: { from: 'board' } });
                    }}
                    title={txt.createFullMeetup}
                  >
                    + {txt.createMeetup}
                  </button>
                </div>

                <div className="social-meetups-meetups-list">
                  {meetups.length === 0 && (
                    <p className="social-meetups-placeholder">{txt.meetupsEmpty}</p>
                  )}

                  {meetups.map((m) => {
                    const organizer =
                      m?.organizer || m?.createdBy || m?.owner || m?.userId || m?.trainerId || null;

                    const canEdit = organizer ? String(organizer) === myUserId : true;

                    return (
                      <article
                        key={m._id}
                        className={`social-meetup-card${draggingMeetupId === m._id ? ' social-meetup-card--dragging' : ''}`}
                        draggable
                        onDragStart={(e) => handleDragStartMeetup(e, m._id)}
                        onDragEnd={handleDragEnd}
                        style={{ cursor: 'grab', userSelect: 'none' }}
                      >
                        <header className="social-meetup-card-header">
                          <h3>{m.title || txt.untitledMeetup}</h3>
                          {m.sport && <span className="social-meetup-tag">{m.sport}</span>}
                        </header>

                        <div className="social-meetup-card-body">
                          {m.dateTime && (
                            <div className="social-meetup-meta">
                              🗓{' '}
                              {new Date(m.dateTime).toLocaleString(locale, {
                                dateStyle: 'short',
                                timeStyle: 'short',
                              })}
                            </div>
                          )}

                          {(m.meetingCity || m.meetingCommune) && (
                            <div className="social-meetup-meta">
                              📍 {[m.meetingCommune, m.meetingCity].filter(Boolean).join(' / ')}
                            </div>
                          )}

                          {m.distanceKm ? (
                            <div className="social-meetup-meta">📏 {m.distanceKm} km</div>
                          ) : null}

                          {m.description && <p className="social-meetup-description">{m.description}</p>}

                          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              className="social-cta-secondary"
                              style={{ borderRadius: 999 }}
                              title="Abrir el detalle del encuentro y su chat grupal"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate(`/social/meetups/${m._id}`);
                              }}
                            >
                              Ver / Chat
                            </button>

                            <button
                              type="button"
                              className="social-cta-secondary"
                              style={{ borderRadius: 999 }}
                              disabled={!canEdit}
                              title={!canEdit ? txt.noPermissionEdit : txt.fullFormEdit}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setError('');
                                setMessage('');

                                navigate('/social/meetups', {
                                  state: {
                                    from: 'board',
                                    meetup: m,
                                    groupId: m.groupId || null,
                                  },
                                });
                              }}
                            >
                              {txt.edit}
                            </button>

                            <button
                              type="button"
                              className="social-cta-secondary"
                              style={{ borderRadius: 999 }}
                              disabled={!canEdit}
                              title={!canEdit ? txt.noPermissionEdit : txt.deleteMeetupTitle}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setError('');
                                setMessage('');
                                setDeleteMeetup(m);
                              }}
                            >
                              {txt.delete}
                            </button>
                          </div>
                        </div>

                        <footer className="social-meetup-card-footer">
                          <span>{txt.dragToGroup}</span>
                        </footer>
                      </article>
                    );
                  })}
                </div>
              </section>
            )}

            {showContacts && (
              <section ref={contactsRef} className="social-meetups-column">
                <h2>{txt.contactsTitle}</h2>
                <p className="social-meetups-subtext">{txt.contactsHint}</p>

                <div className="social-meetups-contacts-list">
                  {contacts.length === 0 && (
                    <p className="social-meetups-placeholder">{txt.contactsEmpty}</p>
                  )}

                  {contacts.map((c) => {
                    const id = String(c._id);
                    const dragging = draggingContactId === id;
                    const src = resolveImg(c.photoUrl || null, c.role);

                    return (
                      <div
                        key={id}
                        className="social-meetups-contact-item"
                        draggable
                        onDragStart={(e) => handleDragStartContact(e, id)}
                        onDragEnd={handleDragEnd}
                        style={{ opacity: dragging ? 0.7 : 1, cursor: 'grab', userSelect: 'none' }}
                      >
                        <div className="social-meetups-contact-avatar">
                          <img
                            src={src || undefined}
                            alt={c.name}
                            onError={(e) => {
                              e.currentTarget.src = resolveImg(null, c.role) || '';
                            }}
                            draggable={false}
                          />
                        </div>

                        <div className="social-meetups-contact-info">
                          <div
                            className="social-meetups-contact-name"
                            style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                          >
                            <span>{c.name || c.displayName || txt.athleteFallback}</span>

                            <span
                              style={{
                                fontSize: 11,
                                fontWeight: 900,
                                padding: '3px 8px',
                                borderRadius: 999,
                                background: 'rgba(15, 23, 42, 0.12)',
                              }}
                              title={txt.originTitle(c.sourceLabel || c.source || txt.unknownOrigin)}
                            >
                              {c.sourceLabel || txt.origin}
                            </span>

                            {!c.userId ? <span style={{ marginLeft: 4, fontSize: 12, fontWeight: 900 }}>⚠️</span> : null}
                          </div>

                          <div className="social-meetups-contact-meta">
                            {c.sport && <span>{c.sport}</span>}
                            {c.city && <span> · {c.city}</span>}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.8 }}>
                  {txt.nextStep}
                </div>
              </section>
            )}
          </div>
        )}

        {(showCreateGroup || editGroup) && (
          <div
            className="modal-backdrop"
            onMouseDown={() => {
              setShowCreateGroup(false);
              setEditGroup(null);
            }}
          >
            <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>{editGroup ? txt.editGroupModal : txt.createGroupModal}</h3>

              <input
                placeholder={txt.groupName}
                value={groupForm.name}
                onChange={(e) => setGroupForm((p) => ({ ...p, name: e.target.value }))}
                style={{ width: '100%', padding: 10, borderRadius: 10, marginBottom: 8 }}
              />

              <textarea
                placeholder={txt.description}
                value={groupForm.description}
                onChange={(e) => setGroupForm((p) => ({ ...p, description: e.target.value }))}
                style={{ width: '100%', padding: 10, borderRadius: 10, minHeight: 90 }}
              />

              <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
                <button
                  className="social-cta-secondary"
                  style={{ borderRadius: 999 }}
                  onClick={() => {
                    setShowCreateGroup(false);
                    setEditGroup(null);
                  }}
                >
                  {txt.cancel}
                </button>

                <button
                  className="social-cta"
                  style={{ borderRadius: 999 }}
                  onClick={async () => {
                    try {
                      setError('');
                      setMessage('');

                      if (!groupForm.name.trim()) {
                        throw new Error(txt.groupNameRequired);
                      }

                      if (editGroup) {
                        await updateGroupApi(editGroup._id, groupForm);
                        setMessage(txt.groupUpdated);
                      } else {
                        await createGroupApi(groupForm);
                        setMessage(txt.groupCreated);
                      }

                      setShowCreateGroup(false);
                      setEditGroup(null);
                      await loadData();
                    } catch (e) {
                      setError(String(e?.message || e));
                    }
                  }}
                >
                  {txt.save}
                </button>
              </div>

              <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
                {txt.backendNote}
              </div>
            </div>
          </div>
        )}

        {deleteGroup && (
          <div className="modal-backdrop" onMouseDown={() => setDeleteGroup(null)}>
            <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>{txt.deleteGroupTitle}</h3>
              <p>{txt.deleteGroupConfirm(deleteGroup.name)}</p>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="social-cta-secondary"
                  style={{ borderRadius: 999 }}
                  onClick={() => setDeleteGroup(null)}
                >
                  {txt.cancel}
                </button>

                <button
                  className="social-cta"
                  style={{ borderRadius: 999 }}
                  onClick={async () => {
                    try {
                      setError('');
                      setMessage('');
                      await deleteGroupApi(deleteGroup._id);
                      setDeleteGroup(null);
                      setMessage(txt.groupDeleted);
                      await loadData();
                    } catch (e) {
                      setError(String(e?.message || e));
                    }
                  }}
                >
                  {txt.delete}
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteMeetup && (
          <div className="modal-backdrop" onMouseDown={() => setDeleteMeetup(null)}>
            <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
              <h3 style={{ marginTop: 0 }}>{txt.deleteMeetupTitle}</h3>
              <p>{txt.deleteMeetupConfirm(deleteMeetup.title || txt.thisMeetup)}</p>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button
                  className="social-cta-secondary"
                  style={{ borderRadius: 999 }}
                  onClick={() => setDeleteMeetup(null)}
                >
                  {txt.cancel}
                </button>

                <button
                  className="social-cta"
                  style={{ borderRadius: 999 }}
                  onClick={async () => {
                    try {
                      setError('');
                      setMessage('');
                      await deleteMeetupApi(deleteMeetup._id);
                      setDeleteMeetup(null);
                      setMessage(txt.meetupDeleted);
                      await loadData();
                    } catch (e) {
                      setError(String(e?.message || e));
                    }
                  }}
                >
                  {txt.delete}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}